import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import type { WidgetInstance } from '@trinity/chat';
import type { Segment as SegmentDropdownItem } from '@trinity/chat-components/containers/ChartWidget';
import { useChartWidgetApi } from '@trinity/chat-components/containers/ChartWidget';
import { useCurrentSegmentIdsContext } from '@trinity/chat-components/containers/Segmentation.Dashboard/context';
import { getBlockById } from '@trinity/chat-components/utils';
import DropDown from '../DropDown';
import Input from '../Input';
import { Header, Modal } from '../Modal';
import ButtonSet from '../Modal/ButtonSet';
import { ButtonActivityIndicator } from '../presentation';
import Button from '../presentation/Button';

type BlockDropdownItem = {
  id: string;
  title: string;
};

type SectionDropdownItem = {
  id: string;
  title: string;
};

function resolveBlockTitle(block: { id: string; title?: string; tag?: { tagName?: string } }): string {
  if (block.title !== undefined && block.title.trim() !== '') return block.title;
  if (block.tag?.tagName) return block.tag.tagName;
  return `Block ${String(block.id).slice(-4)}`;
}

function resolveSectionTitle(section: { id: string; title?: string; tag?: { tagName?: string } }): string {
  if (section.title !== undefined) return section.title;
  if (section.tag?.tagName) return section.tag.tagName;
  return `Section ${String(section.id).slice(-4)}`;
}

function canAddWidget(selectedSegment: SegmentDropdownItem | null, selectedBlock: BlockDropdownItem | null, selectedSection: SectionDropdownItem | null, isPending: boolean): boolean {
  return !!(selectedSegment && selectedBlock && selectedSection && !isPending);
}

function SegmentDropdownSection({ segmentDropdownItems, handleSegmentSelect, selectedSegment, isLoading }: {
  segmentDropdownItems: SegmentDropdownItem[];
  handleSegmentSelect: (s: SegmentDropdownItem) => void;
  selectedSegment: SegmentDropdownItem | null;
  isLoading: boolean;
}) {
  return (
    <ModalSection>
      <ModalLabel>Segment Dashboard</ModalLabel>
      <DropDown<SegmentDropdownItem>
        items={segmentDropdownItems}
        onSelect={handleSegmentSelect}
        getItemValue={(item) => item.name}
        text={selectedSegment?.name || 'Select a segment dashboard'}
        disabled={isLoading}
      />
      {isLoading && <LoadingText>Loading segments...</LoadingText>}
    </ModalSection>
  );
}

function BlockDropdownSection({ blockDropdownItems, handleBlockSelect, selectedBlock, selectedSegment }: {
  blockDropdownItems: BlockDropdownItem[];
  handleBlockSelect: (b: BlockDropdownItem) => void;
  selectedBlock: BlockDropdownItem | null;
  selectedSegment: SegmentDropdownItem | null;
}) {
  return (
    <ModalSection>
      <ModalLabel>Block</ModalLabel>
      <DropDown<BlockDropdownItem>
        items={blockDropdownItems}
        onSelect={handleBlockSelect}
        getItemValue={(item) => item.title}
        text={selectedBlock?.title || 'Select a block'}
        disabled={!selectedSegment || blockDropdownItems.length === 0}
      />
      {selectedSegment && blockDropdownItems.length === 0 && (
        <HelperText>No blocks with sections found in the selected segment</HelperText>
      )}
    </ModalSection>
  );
}

function SectionDropdownSection({ sectionDropdownItems, handleSectionSelect, selectedSection, selectedBlock }: {
  sectionDropdownItems: SectionDropdownItem[];
  handleSectionSelect: (s: SectionDropdownItem) => void;
  selectedSection: SectionDropdownItem | null;
  selectedBlock: BlockDropdownItem | null;
}) {
  return (
    <ModalSection>
      <ModalLabel>Section</ModalLabel>
      <DropDown<SectionDropdownItem>
        items={sectionDropdownItems}
        onSelect={handleSectionSelect}
        getItemValue={(item) => item.title}
        text={selectedSection?.title || 'Select a section'}
        disabled={!selectedBlock || sectionDropdownItems.length === 0}
      />
      {selectedBlock && sectionDropdownItems.length === 0 && (
        <HelperText>No sections found in the selected block</HelperText>
      )}
    </ModalSection>
  );
}

type WidgetPlacementModalProps = {
  open: boolean;
  onClose: () => void;
  widget: WidgetInstance;
  onPlacementSuccess: () => void;
  disablePortal?: boolean;
};

export const WidgetPlacementModal = ({
  open,
  onClose,
  widget,
  onPlacementSuccess,
  disablePortal,
}: WidgetPlacementModalProps) => {
  const { projectId } = useCurrentSegmentIdsContext();

  const [selectedSegment, setSelectedSegment] =
    useState<SegmentDropdownItem | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<BlockDropdownItem | null>(
    null,
  );
  const [selectedSection, setSelectedSection] =
    useState<SectionDropdownItem | null>(null);
  const [position, setPosition] = useState<string>('');

  // Use the API context
  const { useGetSegments, useAddWidgetToSection } = useChartWidgetApi();

  // Fetch segments for the project
  const segmentsQuery = useGetSegments(projectId, open && !!projectId);

  // Create dropdown items from segments
  const segmentDropdownItems = useMemo<SegmentDropdownItem[]>(() => {
    if (!segmentsQuery.data?.segments) return [];

    return segmentsQuery.data.segments.map((segment) => ({
      id: segment.id,
      name: segment.name,
      dashboard: segment.dashboard,
    }));
  }, [segmentsQuery.data?.segments]);

  // Create block dropdown items from selected segment (only blocks with sections)
  const blockDropdownItems = useMemo<BlockDropdownItem[]>(() => {
    if (!selectedSegment?.dashboard?.blocks) return [];

    return selectedSegment.dashboard.blocks
      .filter((block) => block.sections && block.sections.length > 0)
      .map((block) => ({
        id: block.id,
        title: resolveBlockTitle(block),
      }));
  }, [selectedSegment]);

  // Create section dropdown items from selected block
  const sectionDropdownItems = useMemo<SectionDropdownItem[]>(() => {
    if (!selectedSegment?.dashboard?.blocks || !selectedBlock) return [];

    const block = getBlockById(selectedSegment.dashboard, selectedBlock.id);
    if (!block?.sections) return [];

    return block.sections.map((section) => ({
      id: section.id,
      title: resolveSectionTitle(section),
    }));
  }, [selectedSegment, selectedBlock]);

  // Reset dependent dropdowns when segment changes
  useEffect(() => {
    if (selectedSegment) {
      setSelectedBlock(null);
      setSelectedSection(null);
      setPosition('');
    }
  }, [selectedSegment]);

  // Reset dependent dropdowns when block changes
  useEffect(() => {
    if (selectedBlock) {
      setSelectedSection(null);
      setPosition('');
    }
  }, [selectedBlock]);

  // Add widget to dashboard section mutation
  const addWidgetMutation = useAddWidgetToSection(projectId);

  const handleClose = useCallback(() => {
    setSelectedSegment(null);
    setSelectedBlock(null);
    setSelectedSection(null);
    setPosition('');
    addWidgetMutation.reset();
    onClose();
  }, [onClose, addWidgetMutation]);

  // Handle success separately to maintain the same interface
  useEffect(() => {
    if (addWidgetMutation.isSuccess) {
      onPlacementSuccess();
      handleClose();
    }
  }, [addWidgetMutation.isSuccess, onPlacementSuccess, handleClose]);

  const handleSegmentSelect = useCallback((segment: SegmentDropdownItem) => {
    setSelectedSegment(segment);
  }, []);

  const handleBlockSelect = useCallback((block: BlockDropdownItem) => {
    setSelectedBlock(block);
  }, []);

  const handleSectionSelect = useCallback((section: SectionDropdownItem) => {
    setSelectedSection(section);
  }, []);

  const handlePositionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Allow empty string or positive numbers
      if (value === '' || (/^\d+$/.test(value) && parseInt(value, 10) >= 0)) {
        setPosition(value);
      }
    },
    [],
  );

  const handleAddWidget = () => {
    if (!selectedSegment || !selectedBlock || !selectedSection || !widget)
      return;

    const insertPosition = position === '' ? -1 : parseInt(position, 10);

    addWidgetMutation.mutate({
      segmentId: selectedSegment.id,
      sectionId: selectedSection.id,
      widgetId: widget.id,
      insertPosition,
    });
  };

  const canAdd = canAddWidget(selectedSegment, selectedBlock, selectedSection, addWidgetMutation.isPending);

  return (
    <Modal open={open} onClose={handleClose} className="widget-placement-modal" disablePortal={disablePortal}>
      <div style={{ minWidth: 450, maxWidth: 600 }}>
        <Header>Add to Dashboard</Header>

        <SegmentDropdownSection
          segmentDropdownItems={segmentDropdownItems}
          handleSegmentSelect={handleSegmentSelect}
          selectedSegment={selectedSegment}
          isLoading={segmentsQuery.isLoading}
        />

        <BlockDropdownSection
          blockDropdownItems={blockDropdownItems}
          handleBlockSelect={handleBlockSelect}
          selectedBlock={selectedBlock}
          selectedSegment={selectedSegment}
        />

        <SectionDropdownSection
          sectionDropdownItems={sectionDropdownItems}
          handleSectionSelect={handleSectionSelect}
          selectedSection={selectedSection}
          selectedBlock={selectedBlock}
        />

        <ModalSection>
          <ModalLabel>Order (optional)</ModalLabel>
          <Input
            name="widget-position"
            type="number"
            min={0}
            value={position}
            onChange={handlePositionChange}
            placeholder="Leave empty to add at the end"
            style={{ width: '100%' }}
          />
          <HelperText>
            Enter the position where you want to insert the widget (0 = first
            position)
          </HelperText>
        </ModalSection>

        <ButtonSet>
          <Button.Destructive variant="brick" onClick={handleClose}>
            Cancel
          </Button.Destructive>
          <ButtonActivityIndicator
            indicatorSize={12}
            disabled={!canAdd}
            implicitDisable={addWidgetMutation.isPending}
            loading={addWidgetMutation.isPending}
            variant="brick"
            onClick={handleAddWidget}
          >
            Add
          </ButtonActivityIndicator>
        </ButtonSet>
      </div>
    </Modal>
  );
};

const ModalSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  marginBottom: '16px',
});

const ModalLabel = styled.div(({ theme }) => ({
  fontSize: 18,
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.text.primary,
}));

const LoadingText = styled.div({
  fontSize: 12,
  color: 'var(--gray-d)',
  fontStyle: 'italic',
});

const HelperText = styled.div({
  fontSize: 11,
  color: 'var(--gray-d)',
  marginTop: '4px',
});
